// WalletPage.js
import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import Header from '../components/Header';
import Navigation from '../components/Navigation';
import dogecoinLogo from '../assets/images/dogecoin-logo.svg';
import { Icon } from '@iconify/react';
import { TelegramUserContext } from '../App';
import { useUserData } from '../contexts/UserDataContext';
import { useTonAddress, useTonConnectUI } from '@tonconnect/ui-react';

const Container = styled.div`
  width: 100%;
  max-width: 100%;
  margin: 0;
  padding: 0;
  background-color: #ffffff;
  height: 100vh;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  font-size: 13px;
  overflow: hidden;
  position: relative;
  padding-top: 70px; /* Account for fixed header */
  padding-bottom: 80px; /* Account for fixed navigation */
  
  @media (max-width: 375px) {
    font-size: 12px;
    padding-top: 60px;
    padding-bottom: 70px;
  }
`;

const HeaderWrapper = styled.div`
  /* Header is now fixed, no need for wrapper positioning */
`;

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 90px; /* Increased padding to ensure content is fully scrollable */
  -webkit-overflow-scrolling: touch;
  position: relative;
  height: calc(100vh - 60px); /* Use viewport height minus header */
  overscroll-behavior: none; /* Prevent pull-to-refresh and overscroll */
  -webkit-overscroll-behavior: none;
  touch-action: pan-y; /* Allow vertical scrolling but prevent pull gestures */
  
  @media (max-width: 375px) {
    height: calc(100vh - 55px);
    padding-bottom: 85px;
  }
`;

const FooterWrapper = styled.div`
  position: fixed;
  bottom: 0;
  z-index: 100;
  width: 100%;
  background-color: white;
  max-width: 100%;
  flex-shrink: 0;
  box-sizing: border-box;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin: 16px 20px 12px;
  color: #202020;
`;

const WalletSection = styled.div`
  background-color: white;
  margin: 0 16px 12px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const BalanceItem = styled.div`
  display: flex;
  align-items: center;
  padding: 14px 16px;
  justify-content: space-between;
  
  &:not(:last-child) {
    border-bottom: 1px solid #f0f0f0;
  }
`;

const CoinInfo = styled.div`
  display: flex;
  align-items: center;
`;

const CoinLogo = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 16px;
  background-color: ${props => props.$bgColor || '#f0f0f0'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: white;
  font-size: 12px;
`;

const CoinDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const CoinSymbol = styled.div`
  font-size: 12px;
  color: #757575;
  margin-bottom: 2px;
`;

const CoinAmount = styled.div`
  font-size: 16px;
  font-weight: 600;
`;

const CoinValue = styled.div`
  font-size: 12px;
  color: #757575;
`;

const WithdrawButton = styled.button`
  background-color: black;
  color: white;
  border: none;
  border-radius: 16px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
`;

const InfoButton = styled.button`
  background-color: transparent;
  color: #757575;
  border: none;
  font-size: 13px;
  padding: 0;
  cursor: pointer;
`;

const ReceivingWallet = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
`;

const WalletAddress = styled.div`
  font-size: 14px;
  flex-grow: 1;
  text-align: center;
  font-weight: 500;
`;

const CopyButton = styled.button`
  background-color: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  margin-left: 12px;
  
  svg {
    width: 18px;
    height: 18px;
    fill: #757575;
  }
`;

const NoRecords = styled.div`
  text-align: center;
  color: #757575;
  padding: 20px 0;
  font-size: 14px;
`;

const UserInfoSection = styled.div`
  margin: 16px 16px 12px;
  background-color: white;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const UserAvatar = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background-color: #f0f0f0;
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const UserName = styled.div`
  font-size: 18px;
  font-weight: 600;
`;

const UserId = styled.div`
  font-size: 12px;
  color: #757575;
  margin-top: 2px;
`;

// Toast notification styles
const ToastContainer = styled.div`
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background-color: #4caf50;
  color: white;
  padding: 14px 20px;
  border-radius: 25px;
  font-size: 14px;
  font-weight: 500;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  animation: ${props => props.$show ? 'slideInDown' : 'slideOutUp'} 0.3s ease-in-out;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 250px;
  justify-content: center;
  
  @keyframes slideInDown {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }
  
  @keyframes slideOutUp {
    from {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
    to {
      opacity: 0;
      transform: translateX(-50%) translateY(-20px);
    }
  }
`;

const CheckIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: rgba(55, 238, 31, 0.3);
  position: relative;
  flex-shrink: 0;
  
  &::after {
    content: '';
    width: 5px;
    height: 8px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
    margin-bottom: 1px;
  }
`;

const ConnectWalletButton = styled.button`
  background: linear-gradient(135deg, #0088CC 0%, #005BB5 100%);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 16px 20px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    background: linear-gradient(135deg, #0077BB 0%, #004499 100%);
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const WalletConnectedInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ConnectedStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #4CAF50;
  font-weight: 500;
  font-size: 14px;
`;

const StatusIndicator = styled.div`
  width: 8px;
  height: 8px;
  background-color: #4CAF50;
  border-radius: 50%;
`;

const DisconnectButton = styled.button`
  background: transparent;
  color: #666;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f5f5f5;
    border-color: #bbb;
  }
`;

const WalletPage = ({ onNavigate }) => {
  // Get Telegram user information from context
  const telegramUser = useContext(TelegramUserContext);
  const { userData } = useUserData();
  
  // Toast state
  const [showToast, setShowToast] = useState(false);
  
  // TON Connect hooks
  const tonAddress = useTonAddress();
  const [tonConnectUI] = useTonConnectUI();
  
  const isWalletConnected = !!tonAddress;
  
  const handleConnectWallet = () => {
    tonConnectUI.openModal();
  };
  
  const handleDisconnectWallet = () => {
    tonConnectUI.disconnect();
  };
  
  const handleCopyAddress = () => {
    if (tonAddress) {
      navigator.clipboard.writeText(tonAddress);
      setShowToast(true);
      setTimeout(() => {
        setShowToast(false);
      }, 3000);
    }
  };
  
  // Format TON address for display (show first 6 and last 4 characters)
  const formatTonAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  // Generate initials for avatar if no photo is available
  const getInitials = () => {
    if (telegramUser.firstName) {
      return telegramUser.firstName.charAt(0).toUpperCase() + 
        (telegramUser.lastName ? telegramUser.lastName.charAt(0).toUpperCase() : '');
    }
    return telegramUser.username.charAt(0).toUpperCase();
  };
  return (
    <Container>
      {/* Toast Notification */}
      {showToast && (
        <ToastContainer $show={showToast}>
          <CheckIcon />
          TON wallet address copied to clipboard!
        </ToastContainer>
      )}
      
      <HeaderWrapper>
        <Header dogeBalance={userData?.balance || 0} pxlBalance={userData?.pxl_balance || 0} />
      </HeaderWrapper>
      <ScrollableContent>
        {/* User Information Section */}
        <UserInfoSection>
          <UserAvatar>
            {telegramUser.photoUrl ? (
              <img src={telegramUser.photoUrl} alt={telegramUser.username} />
            ) : (
              getInitials()
            )}
          </UserAvatar>
          <div>
            <UserName>
              {telegramUser.username !== 'User' ? `@${telegramUser.username}` : 
                (telegramUser.firstName ? `${telegramUser.firstName} ${telegramUser.lastName || ''}`.trim() : 'Guest User')}
            </UserName>
            {telegramUser.id && <UserId>ID: {telegramUser.id}</UserId>}
          </div>
        </UserInfoSection>
        
        <SectionTitle>Wallet Balance</SectionTitle>
        <WalletSection>
          <BalanceItem>
            <CoinInfo>
              <CoinLogo $bgColor="transparent">
                <Icon icon="simple-icons:ton" width="35" height="35" color="#0088CC" />
              </CoinLogo>
              <CoinDetails>                <CoinSymbol>$PXL</CoinSymbol>
                <CoinAmount>{userData?.pxl_balance || 0}</CoinAmount>
              </CoinDetails>
            </CoinInfo>
            <InfoButton>What is $PXL?</InfoButton>
          </BalanceItem>
          <BalanceItem>
            <CoinInfo>
              <CoinLogo $bgColor="#F2A900">
                <img src={dogecoinLogo} alt="DOGE" width="24" height="24" />
              </CoinLogo>
              <CoinDetails>
                <CoinSymbol>DOGE</CoinSymbol>
                <CoinAmount>{(userData?.balance || 0).toFixed(2)}</CoinAmount>
                <CoinValue>≈ ${((userData?.balance || 0) * 0.175).toFixed(2)}</CoinValue>
              </CoinDetails>
            </CoinInfo>
            <WithdrawButton>Withdraw</WithdrawButton>
          </BalanceItem>
        </WalletSection>
        
        <SectionTitle>TON Wallet</SectionTitle>
        <WalletSection>
          {isWalletConnected ? (
            <ReceivingWallet>
              <WalletConnectedInfo>
                <ConnectedStatus>
                  <StatusIndicator />
                  Wallet Connected
                </ConnectedStatus>
                <WalletAddress>{formatTonAddress(tonAddress)}</WalletAddress>
              </WalletConnectedInfo>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <CopyButton onClick={handleCopyAddress}>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                  </svg>
                </CopyButton>
                <DisconnectButton onClick={handleDisconnectWallet}>
                  Disconnect
                </DisconnectButton>
              </div>
            </ReceivingWallet>
          ) : (
            <div style={{ padding: '16px' }}>
              <ConnectWalletButton onClick={handleConnectWallet}>
                <Icon icon="simple-icons:ton" width="20" height="20" />
                Connect TON Wallet
              </ConnectWalletButton>
            </div>
          )}
        </WalletSection>
        
        <SectionTitle>Withdrawal Record</SectionTitle>
        <WalletSection>
          <NoRecords>No records</NoRecords>
        </WalletSection>
      </ScrollableContent>
      <FooterWrapper>
        <Navigation activePage="wallet" onNavigate={onNavigate} />
      </FooterWrapper>
    </Container>
  );
};

export default WalletPage;
